import fp from 'lodash/fp'

const ID_ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyz'
const ID_ALPHABET_LENGTH = ID_ALPHABET.length
const ID_SAFE_BYTE_CUTOFF = 256 - (256 % ID_ALPHABET_LENGTH)
const DEFAULT_ID_SIZE = 10

let fallbackIdCounter = 0

const getCrypto = () => {
  if (globalThis.window !== undefined) {
    return globalThis.crypto || null
  }

  return null
}

const getRandomBytes = (size) => {
  const cryptoObj = getCrypto()

  if (!cryptoObj?.getRandomValues) {
    return null
  }

  return cryptoObj.getRandomValues(new Uint8Array(size))
}

const generateFallbackId = (size) => {
  fallbackIdCounter += 1

  return `${Date.now().toString(36)}${fallbackIdCounter.toString(36)}`
    .slice(-size)
    .padStart(size, '0')
}

const toInt32 = (value) => {
  const numericValue = Number(value)

  if (!Number.isFinite(numericValue) || numericValue === 0) {
    return 0
  }

  const truncatedValue = Math.trunc(numericValue)
  const twoToThirtyTwo = 4294967296
  let unsignedValue = truncatedValue % twoToThirtyTwo

  if (unsignedValue < 0) {
    unsignedValue += twoToThirtyTwo
  }

  return unsignedValue >= 2147483648
    ? unsignedValue - twoToThirtyTwo
    : unsignedValue
}

/**
 * Generates integer hash from string
 * @param {string} message That should be hashed
 * @returns {number} Hash
 */
export const hashMessage = (message) => {
  let hash = 0

  for (let i = message.length; i >= 0; i -= 1) {
    const charCode = message.codePointAt(i)

    hash = (hash << 5) - hash + charCode
    hash = toInt32(hash)
  }

  return hash
}

export const genId = (size = DEFAULT_ID_SIZE) => {
  const bytes = getRandomBytes(size * 2)

  if (!bytes) {
    return generateFallbackId(size)
  }

  let id = ''

  while (id.length < size) {
    const randomBytes = id.length === 0 ? bytes : getRandomBytes(size * 2)

    if (!randomBytes) {
      return `${id}${generateFallbackId(size)}`.slice(0, size)
    }

    for (let i = 0; i < randomBytes.length && id.length < size; i += 1) {
      const byte = randomBytes[i]

      if (byte < ID_SAFE_BYTE_CUTOFF) {
        id += ID_ALPHABET[byte % ID_ALPHABET_LENGTH]
      }
    }
  }

  return id
}

export const abbrv = (phrase) => {
  if (!phrase) {
    return phrase
  }

  const items = phrase.split(/\s/)

  return items.map((words) => words.charAt(0).toUpperCase()).join('')
}

export const toId = fp.flow(fp.split(/\s/), fp.join('-'), fp.toLower)

export const splitBySentences = (phrase, sentenceCount = 2) => {
  if (!phrase) {
    return []
  }

  const expectedSentenceLength = Math.ceil(phrase.length / sentenceCount)
  const words = phrase.split(/\s+/)
  const sentences = []
  let sentenceBuffer = []
  let actualSentenceLength = 0

  for (let i = 0, wl = words.length; i < wl; i += 1) {
    const word = words[i]

    actualSentenceLength += word.length

    if (
      expectedSentenceLength <= actualSentenceLength &&
      sentenceBuffer.length
    ) {
      actualSentenceLength = 0
      sentences.push(sentenceBuffer.join(' '))
      sentenceBuffer = []
    }

    sentenceBuffer.push(word)
  }

  if (sentenceBuffer.length) {
    sentences.push(sentenceBuffer.join(' '))
  }

  return sentences
}
